import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { List } from 'immutable';

import connectOptions, { mergeProps } from '../../../utils/connectOptions';
import Marketing from '../../../components/Company/CompanyBuyer/Marketing';
import { PrettyErrorProvider } from '../../../contexts/PrettyErrorContext';
import copyToClipboard from '../../../utils/copyToClipboard';
import { checkAccess } from '../../../utils/checkPermissions';
import prepareDetail from '../target/entitiesApiPrepape';
import confirm from '../../decorators/confirm';
import stopPropagation from '../../../decorators/stopPropagation';
import { orderContactService } from '../../services/contact';
import { fetchEmailTemplates } from '../../../actions/emailTemplates';
import { enterCompanyDealNote, leaveCompanyDealNote } from '../../../actions/company/companyEvents';
import configureEmailMergedFields from '../../../utils/configureEmailMergedFields';
import getContactMailLink from '../../../utils/getContactMailLink';
import {
  openContact,
  handleUpdateCompany,
  addDetailEntity,
  deleteDetailEntity,
  startEditDetailEntity,
  saveInfo,
  closeValidationError,
  saveCompanyDetailEntity,
  deleteContactChannel,
  deleteContact,
  reorderContacts,
  reorderChannels,
  findDirectors,
  findUsers,
  revertChanges,
  deleteFile,
  getFiles,
  uploadFile,
} from '../../../actions/company/buyer/marketing';
import { unwrap, getChanged, isDeepChanged, toJS, getIn } from '../../../utils/ChangeSpy';
import { saveBIC, updateBuyerIndustries } from '../../../actions/company/buyer/criteria';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES } from '../../../utils/industryTagsHelper';
import AutoCompleteContainer from '../../AutoComplete';
import { usePopupContext } from '../../../contexts/PopupContext';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

const emptyList = List();

const openEmail = (event, { fullLink }) => {
  window.location.href = fullLink;
};

const copyContact = (event, params) => {
  event.stopPropagation();
  copyToClipboard(params.value);
};

const getCompanyName = company =>
  unwrap(company.get('abbrName')) ? company.get('abbrName') : company.get('legalName');

const getBuyerName = buyer => {
  if (!buyer) return null;

  return unwrap(buyer.get('dsplBuyerAbbrName')) ? buyer.get('dsplBuyerAbbrName') : buyer.get('dsplBuyerLegalName');
};

const MarketingContainer = props => {
  const {
    match,
    user,
    activeBuyer,
    companyTarget,
    companyInfo,
    companyName,
    company,
    criteria,
    buyerType,
    inputErrors,
    files,
    dealNotes,
    openedContact,
    contacts,
    emailTemplates,
    isDuplicateCompany,
    enterCompanyDealNote,
    leaveCompanyDealNote,
    openContact,
    handleUpdateCompany,
    addDetailEntity,
    deleteDetailEntity,
    startEditDetailEntity,
    saveInfo,
    saveBIC,
    closeValidationError,
    saveCompanyDetailEntity,
    deleteContactChannel,
    deleteContact,
    reorderContacts,
    fetchEmailTemplates,
    findDirectors,
    findUsers,
    revertChanges,
    updateBuyerIndustries,
    deleteFile,
    getFiles,
    uploadFile,
    reorderChannels,
  } = props;

  const popupContext = usePopupContext();
  const { openPopup, closePopup } = popupContext;
  const { addOnSaveCallback, removeOnSaveCallback, onEditEvent } = useEntityEventContext();

  const { companyId } = match.params;

  const [hasPermissions, setHasPermissions] = useState(false);
  const [industryCategory, setIndustryCategory] = useState('');
  const [businessModel, setBusinessModel] = useState('');

  const canEditData = !isDuplicateCompany;

  const details = useMemo(() => company.filter((v, key) => ['harvcoTags'].indexOf(key) !== -1), [
    company.get('harvcoTags'),
  ]);

  const companyRef = useRef(company);
  const criteriaRef = useRef(criteria);
  const companyIdRef = useRef(companyId);
  const userRef = useRef(user);

  companyRef.current = company;
  criteriaRef.current = criteria;
  companyIdRef.current = companyId;
  userRef.current = user;

  const checkIndustriesValidation = useCallback((ids, businessModelIds) => {
    let result = true;

    if (!ids.length) {
      setIndustryCategory('Industry Tags is required');
      result = false;
    }

    if (!businessModelIds.length) {
      setBusinessModel('Business Model is required');
      result = false;
    }

    return result;
  }, []);

  const handleDetailSubmit = useCallback(() => {
    const detailName = 'harvcoTags';
    const base = companyRef.current.get(detailName);

    if (!isDeepChanged(base)) return;
    if (!companyRef.current.get(`${detailName}IsValid`, true)) return;

    const body = toJS(base);

    body.forEach(entity => {
      Object.keys(entity).forEach(key => {
        if (entity[key] instanceof moment) {
          try {
            entity[key] = entity[key].format('YYYY-MM-DD');
          } catch (error) {
            console.error("Can't transform ", key, entity[key], error);
          }
        }
      });
    });

    saveCompanyDetailEntity({
      companyId: companyIdRef.current,
      name: detailName,
      body,
      realBody: prepareDetail(detailName, body, userRef.current.get('id')),
    });
  }, [saveCompanyDetailEntity]);

  const handleMarketingSubmit = useCallback(
    event => {
      event.preventDefault();
      if (!companyRef.current.get('isValid')) return;

      const body = getChanged(companyRef.current);
      const tags = getChanged(criteriaRef.current);

      const industryCategories = toJS(criteriaRef.current.get(INDUSTRY_CATEGORIES));
      const businessModels = toJS(criteriaRef.current.get(BUSINESS_MODELS));

      const criteriaBody = { businessModels, industryCategories };

      if (
        checkIndustriesValidation(
          industryCategories.map(({ id }) => id),
          businessModels.map(({ id }) => id),
        )
      ) {
        if (Object.keys(tags).length) {
          const bic = criteriaRef.current.get('bic');

          saveBIC({
            companyId: companyIdRef.current,
            data: bic
              .filter(b => b.get('industryCategoryId') > 0)
              .map(b => ({
                industryCategoryId: getIn(b, 'industryCategoryId', ''),
                contactId: getIn(b, 'contactId') || null,
                notes: getIn(b, 'notes') || '',
                priority: 0,
              }))
              .toJS(),
            afterSuccess: () => {},
            tags: criteriaBody,
          });
        }

        if (Object.keys(body).length) {
          if ('buyerLastResearchedDate' in body) {
            try {
              body.buyerLastResearchedDate = body.buyerLastResearchedDate.format('YYYY-MM-DD');
            } catch (error) {
              console.error(error);
            }
          }

          saveInfo({ companyId: companyIdRef.current, body });
        }

        handleDetailSubmit();
      }
    },
    [checkIndustriesValidation, saveBIC, saveInfo, handleDetailSubmit],
  );

  useEffect(() => {
    setHasPermissions(checkAccess(user.getIn(['roles', 0])));
    addOnSaveCallback(handleMarketingSubmit);
    getFiles({ companyId });

    return () => {
      removeOnSaveCallback(handleMarketingSubmit);
      revertChanges();
    };
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const clearFormError = useCallback(() => {
    setBusinessModel('');
    setIndustryCategory('');
  }, []);

  const handleUpdateTags = useCallback(() => {
    const tags = unwrap(criteria.get(INDUSTRY_CATEGORIES));
    const models = unwrap(criteria.get(BUSINESS_MODELS));

    openPopup('TagsManagementPopup', {
      addNew: false,
      clientView: true,
      selectedTags: tags.reduce((accumulator, t) => {
        accumulator[t.get('id')] = true;

        return accumulator;
      }, {}),
      selectedModels: models.reduce((accumulator, m) => {
        accumulator[m.get('id')] = true;

        return accumulator;
      }, {}),
      callback: (tagSelecteds, modelSelecteds) => {
        updateBuyerIndustries(tagSelecteds, modelSelecteds);
      },
    });
  }, [criteria, openPopup, updateBuyerIndustries]);

  const handleDialNotesMouseEnter = useCallback(
    (event, eventId) => {
      enterCompanyDealNote({ eventId });
    },
    [enterCompanyDealNote],
  );

  const handleDialNotesMouseLeave = useCallback(
    (event, eventId) => {
      leaveCompanyDealNote({ eventId });
    },
    [leaveCompanyDealNote],
  );

  const handleOpenContact = useCallback(
    (event, index) => {
      openContact({ index });
    },
    [openContact],
  );

  const handleChange = useCallback(
    ({ name, value }) => {
      handleUpdateCompany({ name, value });
    },
    [handleUpdateCompany],
  );

  const handleFormChange = useCallback(
    ({ target: { name, value } }) => {
      handleChange({ name, value });
    },
    [handleChange],
  );

  const deleteContactAfterConfirm = useCallback(
    (event, contact) => {
      const contactId = contact.id;
      const { entityContactsId } = contact;

      deleteContact({ contactId, entityContactsId, companyId }, closePopup);
    },
    [companyId, deleteContact, closePopup],
  );

  const handleDeleteContact = useCallback(
    stopPropagation(confirm('Delete contact?', deleteContactAfterConfirm, popupContext)),
    [deleteContactAfterConfirm, popupContext],
  );

  const deleteContactChannelAfterConfirm = useCallback(
    (event, channelType, channelIndex) => {
      const contactId = contacts.getIn([openedContact, 'id']);
      const channelId = contacts.getIn([openedContact, channelType, channelIndex, 'id']);

      deleteContactChannel({ contactId, type: channelType, channelId }, closePopup);
    },
    [contacts, openedContact, deleteContactChannel, closePopup],
  );

  const handleDeleteContactChannel = useCallback(
    stopPropagation(confirm('Delete this field from contact?', deleteContactChannelAfterConfirm, popupContext)),
    [deleteContactChannelAfterConfirm, popupContext],
  );

  const handleEditContact = useCallback(() => {
    openPopup('EditContactPopup', {
      openedContact,
      mode: 'buyer',
    });
  }, [openedContact, openPopup]);

  const handleAddContact = useCallback(() => {
    openPopup('AddCompanyContactPopup', { mode: 'buyer' });
  }, [openPopup]);

  const handleEditContactChannel = useCallback(
    (channelType, channelIndex) => {
      const contactId = contacts.getIn([openedContact, 'id']);
      const channelId = contacts.getIn([openedContact, channelType, channelIndex, 'id']);

      if (channelType === 'phones') {
        openPopup('EditPhoneContactChannelPopup', {
          contactId,
          channelType,
          channelIndex,
          openedContact,
          channelId,
          mode: 'buyer',
        });
      } else {
        openPopup('EditEmailContactChannelPopup', {
          contactId,
          channelType,
          channelIndex,
          openedContact,
          channelId,
          mode: 'buyer',
        });
      }
    },
    [contacts, openedContact, openPopup],
  );

  const getEmailMergedFields = useCallback(() => {
    const contact = companyInfo.getIn(['entityContacts', 0]);
    const products = details.get('targetProducts');
    const markets = details.get('targetMarkets');

    return configureEmailMergedFields(contact, contacts, activeBuyer, companyInfo, markets, products, companyTarget);
  }, [companyInfo, contacts, activeBuyer, companyTarget, details]);

  const handleOpenEmail = useCallback(
    (email, template) => {
      const mergedFields = getEmailMergedFields();
      const contact = contacts.get(openedContact);
      const mailLink = getContactMailLink(email, template, mergedFields, user, contact);

      window.open(mailLink, '_blank');
    },
    [getEmailMergedFields, contacts, openedContact, user],
  );

  const handleDetailAdd = useCallback(
    (event, detailName) => {
      addDetailEntity({ name: detailName });
    },
    [addDetailEntity],
  );

  const deleteDetailAfterConfirm = useCallback(
    (event, detailName, data) => {
      const id = unwrap(data.id);
      const entities = company.get(detailName);
      const body = toJS(entities)
        .filter(entity => entity.id !== id)
        .map(entity => {
          Object.keys(entity).forEach(key => {
            if (entity[key] instanceof moment) {
              try {
                entity[key] = entity[key].format('YYYY-MM-DD');
              } catch (error) {
                console.error("Can't transform ", key, entity[key], error);
              }
            }
          });

          return entity;
        });

      deleteDetailEntity(
        {
          name: detailName,
          id,
          companyId,
          body,
          realBody: prepareDetail(detailName, body, user.get('id')),
        },
        closePopup,
      );
    },
    [company, companyId, user, deleteDetailEntity, closePopup],
  );

  const handleDetailDelete = useCallback(
    stopPropagation(confirm('Delete this?', deleteDetailAfterConfirm, popupContext)),
    [deleteDetailAfterConfirm, popupContext],
  );

  const handleDetailEdit = useCallback(
    (event, detailName, detailIndex) => {
      startEditDetailEntity({ name: detailName, index: detailIndex });
    },
    [startEditDetailEntity],
  );

  const handleDetailChange = useCallback(
    event => {
      const {
        target: { name, value },
      } = event;

      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleReorderContacts = useCallback(
    contactsToReorder => {
      const sendToApi = contactsToReorder
        .map(contact => ({
          id: contact.get('id'),
          entityContactsId: contact.get('entityContactsId'),
        }))
        .toJS();

      reorderContacts(contactsToReorder, companyId, sendToApi);
    },
    [companyId, reorderContacts],
  );

  const handleReorderChannels = useCallback(
    contactsToReorder => {
      reorderChannels(contactsToReorder, openedContact);
    },
    [openedContact, reorderChannels],
  );

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      handleChange({ name, value });
    },
    [handleChange],
  );

  const deleteFileAfterConfirm = useCallback(
    (event, file) => {
      deleteFile(
        {
          id: file.get('id'),
          companyId: file.get('companyId'),
        },
        () => {
          closePopup();
          getFiles({ companyId });
        },
      );
    },
    [companyId, deleteFile, getFiles, closePopup],
  );

  const handleDeleteFile = useCallback(stopPropagation(confirm('Delete file?', deleteFileAfterConfirm, popupContext)), [
    deleteFileAfterConfirm,
    popupContext,
  ]);

  const handleUploadFile = useCallback(
    event => {
      const { currentTarget } = event;
      const formData = new FormData(currentTarget);

      event.preventDefault();

      const afterSuccess = () => currentTarget.reset();

      uploadFile({ companyId, formData, afterSuccess });
    },
    [companyId, uploadFile],
  );

  const detailsCallbacks = useMemo(
    () => ({
      onAdd: handleDetailAdd,
      onDelete: handleDetailDelete,
      onChange: handleDetailChange,
      onClick: handleDetailEdit,
    }),
    [handleDetailAdd, handleDetailDelete, handleDetailChange, handleDetailEdit],
  );

  if (!company.get('loaded')) return null;

  const contactsProps = {
    reorderContacts: handleReorderContacts,
    reorderChannels: handleReorderChannels,
    companyName,
    contacts,
    buyerName: getBuyerName(company),
    addContact: handleAddContact,
    deleteContact: handleDeleteContact,
    opened: openedContact,
    openContact: handleOpenContact,
    onCopy: copyContact,
    onDelete: handleDeleteContactChannel,
    onEdit: handleEditContact,
    openEmail,
    onEditChannel: handleEditContactChannel,
    onOpenEmail: handleOpenEmail,
  };

  const businessModels = criteria.get(BUSINESS_MODELS);
  const industryCategories = criteria.get(INDUSTRY_CATEGORIES);

  const suggestResearcher = (
    <AutoCompleteContainer
      idName="buyerLastResearcherId"
      idValue={company.get('buyerLastResearcherId')}
      onChange={handleSuggestChange}
      onFind={findUsers}
      suggestions={company.get('suggests')}
      suggestionsName="suggests"
      value={company.get('suggestResearcher')}
      valueName="suggestResearcher"
    />
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <Marketing
        businessModels={businessModels}
        buyerType={buyerType}
        canEditData={canEditData}
        companyInfo={company}
        contactsProps={contactsProps}
        dealNotes={dealNotes}
        details={details}
        detailsCallbacks={detailsCallbacks}
        emailTemplates={emailTemplates}
        error={{
          businessModel,
          industryCategory,
        }}
        fetchEmailTemplates={fetchEmailTemplates}
        files={files}
        findDirectors={findDirectors}
        hasPermissions={hasPermissions}
        industryCategories={industryCategories}
        onChange={handleFormChange}
        onClick={handleUpdateTags}
        onDeleteFile={handleDeleteFile}
        onEditEvent={onEditEvent}
        onEnterDealNotes={handleDialNotesMouseEnter}
        onErrorClose={clearFormError}
        onFileUpload={handleUploadFile}
        onLeaveDealNotes={handleDialNotesMouseLeave}
        statuses={company.get('statuses')}
        suggestResearcher={suggestResearcher}
        user={user}
      />
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state) {
  const company = state.targetCompany.buyer.marketing;
  const { criteria } = state.targetCompany.buyer;
  const companyInfo = state.targetCompany.info.get('info');
  const companyTarget = state.targetCompany.target;
  const activeBuyer = companyTarget.get('buyers').find(buyer => unwrap(buyer.get('activeBuyer')));

  return {
    user: state.auth.get('user'),
    activeBuyer,
    companyTarget,
    companyInfo,
    companyName: getCompanyName(companyInfo),
    company,
    criteria,
    buyerType: companyInfo.get('buyerType'),
    inputErrors: company.get('inputErrors'),
    files: company.get('files', emptyList),
    dealNotes: state.targetCompany.events.get('buyerDealNotes', emptyList),
    openedContact: company.get('openedContact', 0),
    contacts: company.get('contacts', emptyList),
    emailTemplates: state.emailTemplates.get('data', emptyList),
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
  };
}

MarketingContainer.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        enterCompanyDealNote,
        leaveCompanyDealNote,
        openContact,
        handleUpdateCompany,
        addDetailEntity,
        deleteDetailEntity,
        startEditDetailEntity,
        saveInfo,
        saveBIC,
        closeValidationError,
        saveCompanyDetailEntity,
        deleteContactChannel,
        deleteContact,
        reorderContacts,
        fetchEmailTemplates,
        findDirectors,
        findUsers,
        revertChanges,
        updateBuyerIndustries,
        deleteFile,
        getFiles,
        uploadFile,
      },
      dispatch,
    ),
    reorderChannels: orderContactService(dispatch, reorderChannels),
  };
}

export { MarketingContainer };
export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(MarketingContainer));
